import {
    CurrentRideContract,
    RiderColor,
    UserContentColorHex,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    GeoCoordinate,
    polylineStringToGeoCoordinates,
} from '@polaris-customerweb/sharedcomponents.shared';

export class InProgressRideVm {
    readonly averageElevation: number = 0;
    readonly averageSpeed: number = 0;
    bearing = 0;
    readonly color: RiderColor;
    readonly name: string;
    coordinates: GeoCoordinate[] = [];
    readonly description: string = '';
    readonly elevationChange: number = 0;
    readonly isValid: boolean = false;
    readonly lastUpdatedDate: Date;
    readonly maxElevation: number = 0;
    readonly maxSpeed: number = 0;
    readonly minElevation: number = 0;
    readonly movingTimeSeconds: number = 0;
    readonly polyline: string;
    rideTimeSeconds = 0;
    readonly stoppedTimeSeconds: number = 0;
    readonly totalLengthMeters: number = 0;
    readonly id: string = '';
    readonly vin: string = '';

    constructor(data: Partial<InProgressRideVm> = {}) {
        Object.assign(this, data);
        if (data.lastUpdatedDate) {
            this.lastUpdatedDate = new Date(data.lastUpdatedDate);
        }
    }

    static fromInProgressRideContract(data: CurrentRideContract) {
        if (
            data.resultStatus !== 'success' &&
            data.resultStatus !== 'noLocations'
        ) {
            return new InProgressRideVm({
                isValid: false,
                vin: data.vin,
            });
        }

        const {
            heading: bearing,
            coordinates: polyline = '',
            tripId: id,
            lastUpdatedDate,
            totalDurationSeconds = 0,
            totalHaltTimeSeconds = 0,
            totalRunningTimeMinutes = 0,
            color,
            ...rest
        } = data;

        const coordinates = polylineStringToGeoCoordinates(polyline);

        return new InProgressRideVm({
            bearing,
            color: color as RiderColor,
            coordinates,
            elevationChange:
                (data.maxElevation || 0) - (data.minElevation || 0),
            id,
            isValid: true,
            lastUpdatedDate: lastUpdatedDate
                ? new Date(lastUpdatedDate)
                : undefined,
            movingTimeSeconds: (totalRunningTimeMinutes || 0) * 60,
            polyline,
            rideTimeSeconds: totalDurationSeconds,
            stoppedTimeSeconds: totalHaltTimeSeconds,
            ...rest,
        });
    }

    toGeoJson(): GeoJSON.Feature<GeoJSON.LineString> {
        return {
            geometry: {
                coordinates: this.coordinates.map((coord) =>
                    coord.toCoordinateArray()
                ),
                type: 'LineString' as const,
            },
            properties: {
                bearing: this.bearing,
                color: UserContentColorHex[this.color],
                id: this.id,
                vin: this.vin,
            },
            type: 'Feature' as const,
        };
    }

    prependPreviousCoordinates(previous: InProgressRideVm): void {
        if (previous?.coordinates?.length > 0) {
            this.coordinates = [
                ...previous.coordinates,
                ...(this.coordinates || []),
            ];

            if (!this.bearing) {
                this.bearing = previous.bearing;
            }
        }
    }
}
